import { useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'components/Button';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { RECORD_RETURN_INSPECTION } from 'gql/mutations';
import { REQUEST_RETURN_WAREHOUSE_CONFIRMATION } from 'gql/returnInspection';
import { Return, ReturnStatus } from 'types/return';
import { DEFECT_RESOLUTION_LABELS } from 'types/defectResolution';
import { getStoreCurrencyFormatter } from 'utils';
import { getReturnCurrencyDigits, roundReturnMoney } from 'utils/returnMoney';
import { hasValidReturnReceiptTimestamp } from 'utils/returnReceipt';
import { returnInspectionReasonLabel } from 'utils/returnInspectionReason';

const amountIsValid = (amount: unknown): amount is number =>
  typeof amount === 'number' && Number.isFinite(amount) && amount >= 0;

export function returnRequiresInspection(record?: Return) {
  if (
    [record?.deflectionOption, record?.requestedResolution].some(
      (value) =>
        String(value || '')
          .trim()
          .toUpperCase() === 'KEEP_ITEM_DISCOUNT',
    )
  )
    return false;
  return Boolean(
    (record?.policySnapshot?.inspection &&
      (record.policySnapshot.inspection as any).enabled === true) ||
      record?.meta?.inspection?.required === true,
  );
}

export function validReturnInspection(record: Return, currency: string) {
  const inspection = record.meta?.inspection;
  const valid = Boolean(
    inspection &&
      inspection.version === 1 &&
      inspection.required === true &&
      ['PENDING', 'APPROVED', 'REDUCED', 'DENIED'].includes(
        inspection.status,
      ) &&
      amountIsValid(inspection.requestedAmount) &&
      /^[A-Z]{3}$/.test(inspection.currency || '') &&
      inspection.currency === currency,
  );
  if (!valid || inspection.status === 'PENDING') return valid;
  if (
    typeof inspection.reviewedAt !== 'string' ||
    !Number.isFinite(Date.parse(inspection.reviewedAt)) ||
    typeof inspection.reviewedBy !== 'string' ||
    !inspection.reviewedBy.trim()
  )
    return false;
  if (inspection.status === 'DENIED')
    return (
      typeof inspection.notes === 'string' && Boolean(inspection.notes.trim())
    );
  if (!amountIsValid(inspection.approvedAmount)) return false;
  return inspection.status === 'APPROVED'
    ? inspection.approvedAmount === inspection.requestedAmount
    : inspection.approvedAmount < inspection.requestedAmount &&
        typeof inspection.notes === 'string' &&
        Boolean(inspection.notes.trim());
}

export function returnInspectionBlocksSettlement(
  record?: Return,
  currency = '',
) {
  if (!record || !returnRequiresInspection(record)) return false;
  if (record.meta?.inspection?.warehouseConfirmation?.status === 'REQUESTED') return true;
  if (record.status !== ReturnStatus.RECEIVED || !hasValidReturnReceiptTimestamp(record.meta?.receivedAt))
    return true;
  if (!validReturnInspection(record, currency)) return true;
  const inspection = record.meta!.inspection;
  if (
    !['APPROVED', 'REDUCED'].includes(inspection.status) ||
    !amountIsValid(inspection.approvedAmount)
  )
    return true;
  return inspection.status === 'APPROVED'
    ? inspection.approvedAmount !== inspection.requestedAmount
    : inspection.approvedAmount >= inspection.requestedAmount;
}

export function returnHasScheduledInspectionSettlement(record?: Return) {
  const inspectionSnapshot = record?.policySnapshot?.inspection as any;
  const requested = record?.requestedResolution || record?.deflectionOption || 'REFUND';
  const plan = record?.meta?.defectResolution?.plan;
  const external = plan && ['ACCEPTED', 'COMPLETED'].includes(plan.status) && ['CUSTOM', 'REPLACEMENT_PART'].includes(plan.resolution);
  const rule = (record?.policySnapshot?.settlementTiming as any)?.inspectionRequired?.[requested.includes('STORE_CREDIT') ? 'storeCredit' : 'refund'];
  return Boolean(record && returnRequiresInspection(record) && !external && inspectionSnapshot?.mode !== 'NEVER' && inspectionSnapshot?.policyEnabled !== false &&
    ['APPROVAL', 'FIRST_CARRIER_SCAN', 'INBOUND_DELIVERED', 'WAREHOUSE_RECEIVED', 'INSPECTION_APPROVED'].includes(rule?.resolutionTimingBasis) &&
    Number.isInteger(rule?.resolutionTimingDelayDays) && rule.resolutionTimingDelayDays >= 0);
}

export function ReturnInspectionPanel({
  record,
  currency,
  onSaved,
}: {
  record: Return;
  currency: string;
  onSaved: () => Promise<unknown>;
}) {
  const [outcome, setOutcome] = useState('APPROVE');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [recordInspection] = useMutation(RECORD_RETURN_INSPECTION);
  const [requestWarehouseConfirmation] = useMutation(REQUEST_RETURN_WAREHOUSE_CONFIRMATION);
  const [warehouseNotes, setWarehouseNotes] = useState('');
  const [warehouseError, setWarehouseError] = useState('');
  const [warehouseSaving, setWarehouseSaving] = useState(false);
  const [warehouseSaved, setWarehouseSaved] = useState(false);
  const currentReturnId = useRef<string | null>(record.id);
  currentReturnId.current = record.id;
  useEffect(
    () => () => {
      currentReturnId.current = null;
    },
    [],
  );
  useEffect(() => {
    setOutcome('APPROVE');
    setApprovedAmount('');
    setNotes('');
    setError('');
    setSaved(false);
    setSaving(false);
    setWarehouseNotes('');
    setWarehouseError('');
    setWarehouseSaving(false);
    setWarehouseSaved(false);
  }, [record.id, record.updatedAt]);
  const inspection = record.meta?.inspection;
  const deliveryReview = inspection?.deliveryReview;
  const warehousePending = deliveryReview && inspection?.status === 'PENDING' &&
    inspection?.warehouseConfirmation?.status !== 'REQUESTED' &&
    ![ReturnStatus.RESOLVED, ReturnStatus.DENIED, ReturnStatus.EXPIRED].includes(record.status) &&
    !['PROCESSING', 'COMPLETED', 'OUTCOME_UNKNOWN'].includes(record.settlementStatus || '');
  const requestWarehouse = async () => {
    if (!warehousePending || warehouseSaving || warehouseSaved) return;
    if (!warehouseNotes.trim()) { setWarehouseError('Add a note for the warehouse.'); return; }
    const requestedReturnId = record.id;
    setWarehouseSaving(true); setWarehouseError('');
    try {
      const result = await requestWarehouseConfirmation({variables: {input: {
        returnId: record.id, storeId: record.storeId, expectedUpdatedAt: record.updatedAt, notes: warehouseNotes.trim(),
      }}});
      if (currentReturnId.current !== requestedReturnId) return;
      if (result.data?.requestReturnWarehouseConfirmation?.returnId !== requestedReturnId || result.data?.requestReturnWarehouseConfirmation?.status !== 'AWAITING_WAREHOUSE') throw new Error('Warehouse confirmation was not acknowledged. Refresh and try again.');
      setWarehouseSaved(true); await onSaved();
    } catch (error) {
      if (currentReturnId.current === requestedReturnId) setWarehouseError(error instanceof Error ? error.message : 'Warehouse confirmation could not be requested.');
    } finally { if (currentReturnId.current === requestedReturnId) setWarehouseSaving(false); }
  };
  const valid = validReturnInspection(record, currency);
  const canDecide =
    valid &&
    record.status === ReturnStatus.RECEIVED &&
    hasValidReturnReceiptTimestamp(record.meta?.receivedAt) &&
    inspection?.warehouseConfirmation?.status !== 'REQUESTED' &&
    inspection.status === 'PENDING' &&
    !['PROCESSING', 'COMPLETED', 'OUTCOME_UNKNOWN'].includes(
      record.settlementStatus || '',
    );
  const submit = async () => {
    if (!canDecide || saving || saved) return;
    if (
      outcome === 'REDUCE' &&
      (!approvedAmount.trim() ||
        !amountIsValid(Number(approvedAmount)) ||
        Number(approvedAmount) >= inspection.requestedAmount)
    ) {
      setError(
        'Enter an amount 0 or greater and lower than the requested amount.',
      );
      return;
    }
    if (outcome === 'REDUCE' && roundReturnMoney(Number(approvedAmount), currency) !== Number(approvedAmount)) {
      setError(`Enter an amount with at most ${getReturnCurrencyDigits(currency)} decimal places for ${currency}.`);
      return;
    }
    if (['REDUCE', 'DENY'].includes(outcome) && !notes.trim()) {
      setError(
        outcome === 'DENY'
          ? 'Add a customer-facing reason for denial.'
          : 'Add inspection notes explaining this decision.',
      );
      return;
    }
    setSaving(true);
    setError('');
    const requestReturnId = record.id;
    try {
      await recordInspection({
        variables: {
          input: {
            returnId: record.id,
            storeId: record.storeId,
            expectedUpdatedAt: record.updatedAt,
            outcome,
            ...(outcome === 'REDUCE'
              ? { approvedAmount: Number(approvedAmount) }
              : {}),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          },
        },
      });
      if (currentReturnId.current !== requestReturnId) return;
      setSaved(true);
      await onSaved();
    } catch (error) {
      if (currentReturnId.current !== requestReturnId) return;
      setError(
        error instanceof Error
          ? error.message
          : 'Inspection could not be saved. Refresh and try again.',
      );
    } finally {
      if (currentReturnId.current === requestReturnId) setSaving(false);
    }
  };
  if (!returnRequiresInspection(record)) return null;
  const requested =
    record.requestedResolution || record.deflectionOption || 'REFUND';
  const requestedLabel = requested.startsWith('EXCHANGE')
    ? 'Requested exchange'
    : requested.includes('STORE_CREDIT')
    ? 'Requested store credit'
    : 'Requested refund';
  const plan = record.meta?.defectResolution?.plan;
  const resolution: unknown = plan?.resolution;
  const agreedExternalResolution = plan &&
    ['ACCEPTED', 'COMPLETED'].includes(plan.status) &&
    (resolution === 'CUSTOM' || resolution === 'REPLACEMENT_PART')
      ? DEFECT_RESOLUTION_LABELS[resolution]
      : null;
  const scheduledSettlement = returnHasScheduledInspectionSettlement(record);
  // Keep valid closed inspection results visible while settlement stays blocked.
  const completedExternalInspection = Boolean(
    agreedExternalResolution && plan.status === 'COMPLETED' &&
    record.status === ReturnStatus.RESOLVED && hasValidReturnReceiptTimestamp(record.meta?.receivedAt) &&
    valid && ['APPROVED', 'REDUCED'].includes(inspection.status),
  );
  const closedInspectionDecision = Boolean(
    (record.status === ReturnStatus.RESOLVED || record.settlementStatus === 'COMPLETED') &&
    hasValidReturnReceiptTimestamp(record.meta?.receivedAt) &&
    valid && ['APPROVED', 'REDUCED'].includes(inspection.status),
  );
  const externalInspectionGuidance = completedExternalInspection
    ? 'The agreed resolution is completed. This inspection did not issue a refund or store credit.'
    : 'Complete the agreed resolution separately. This inspection does not issue a refund or store credit.';
  return (
    <section className="mb-6 space-y-4 rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="text-base font-semibold text-gray-900">
        Inspect received items
      </h2>
      {deliveryReview && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-900">
            {inspection?.status === 'PENDING' ? 'Delivered · pending inspection' : 'Delivery review completed'}
          </p>
          {(Array.isArray(deliveryReview.triggerReasons) ? deliveryReview.triggerReasons : []).map((reason: any, index: number) => (
            <p key={index} className="mt-1 text-xs text-amber-800">
              {returnInspectionReasonLabel(reason)}
            </p>
          ))}
          {inspection?.warehouseConfirmation?.status === 'REQUESTED' && (
            <div className="mt-3 text-sm text-amber-900">
              <p>Warehouse confirmation requested.</p>
              <p className="mt-1 text-xs">{inspection.warehouseConfirmation.notes}</p>
            </div>
          )}
          {warehousePending && (
            <div className="mt-3 space-y-3">
              <label className="block text-xs font-medium text-gray-700">
                Warehouse request notes
                <textarea
                  value={warehouseNotes}
                  maxLength={5000}
                  disabled={warehouseSaving || warehouseSaved}
                  onChange={event => setWarehouseNotes(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white p-2 text-sm"
                />
              </label>
              <Button onClick={requestWarehouse} disabled={warehouseSaving || warehouseSaved}>
                Request warehouse confirmation
              </Button>
              {warehouseError && <p role="alert" className="text-sm text-red-700">{warehouseError}</p>}
              {warehouseSaved && <p role="status" className="text-sm text-green-700">Warehouse confirmation requested.</p>}
            </div>
          )}
        </div>
      )}
      {(record.status !== ReturnStatus.RECEIVED || (deliveryReview && !hasValidReturnReceiptTimestamp(record.meta?.receivedAt))) &&
      !['APPROVED', 'REDUCED', 'DENIED'].includes(inspection?.status) ? (
        <p role="status" className="text-sm text-amber-800">
          Inspection starts after the items are marked received. Settlement
          remains on hold.
        </p>
      ) : !valid ? (
        <p role="alert" className="text-sm text-red-700">
          Required inspection details are missing or invalid. Refresh this
          return or contact ShipAid before issuing funds.
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-700">{agreedExternalResolution ? `Agreed resolution: ${agreedExternalResolution}` : requestedLabel}</p>
          <p className="text-sm text-gray-600">
            {agreedExternalResolution ? 'Item value for inspection:' : 'Requested amount:'}{' '}
            {getStoreCurrencyFormatter(currency, inspection.requestedAmount)}{' '}
            {currency}
          </p>
          {agreedExternalResolution && <p className="text-sm text-gray-600">The item value is an inspection assessment, not a refund or store credit.</p>}
          {['APPROVED', 'REDUCED'].includes(inspection.status) &&
          (!returnInspectionBlocksSettlement(record, currency) || closedInspectionDecision) ? (
            <>
              <p className="text-sm font-medium text-green-700">
                {agreedExternalResolution ? 'Approved item value after inspection:' : 'Approved after inspection:'}{' '}
                {getStoreCurrencyFormatter(currency, inspection.approvedAmount)}{' '}
                {currency}
              </p>
              {inspection.notes && (
                <p className="text-sm text-gray-600">{inspection.notes}</p>
              )}
              <p className="text-sm text-gray-600">
                {agreedExternalResolution
                  ? externalInspectionGuidance
                  : closedInspectionDecision
                  ? record.settlementStatus === 'COMPLETED'
                    ? 'The requested resolution is completed. This inspection decision is saved for reference.'
                    : 'This return is closed. This inspection decision is saved for reference.'
                  : scheduledSettlement
                  ? 'Inspection approved. Settlement follows your schedule.'
                  : 'Issue the requested resolution separately when you are ready. No funds were issued by the inspection decision.'}
              </p>
            </>
          ) : inspection.status === 'DENIED' ? (
            <p className="text-sm font-medium text-red-700">
              Denied after inspection. {inspection.notes}
            </p>
          ) : !canDecide ? (
            <p role="status" className="text-sm text-amber-800">
              {inspection?.warehouseConfirmation?.status === 'REQUESTED'
                ? 'Confirm the warehouse request before recording inspection. Settlement remains on hold.'
                : 'Inspection starts after the items are marked received. Settlement remains on hold.'}
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                {agreedExternalResolution
                  ? 'Record the condition of the returned items. This saves the inspection decision only; complete the agreed resolution separately.'
                  : scheduledSettlement
                  ? 'Save the inspection decision. Settlement follows your schedule after approval.'
                  : 'Record the condition of the returned items. This saves the inspection decision only; refunds and store credit require a separate action.'}
              </p>
              <label className="block text-sm font-medium text-gray-700">
                Inspection outcome
                <select
                  value={outcome}
                  disabled={saving || saved}
                  onChange={(event) => {
                    setOutcome(event.target.value);
                    setError('');
                  }}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="APPROVE">
                    {agreedExternalResolution ? 'Approve item condition' : 'Approve the requested resolution'}
                  </option>
                  <option value="REDUCE">{agreedExternalResolution ? 'Reduce the assessed item value' : 'Reduce the approved amount'}</option>
                  <option value="DENY">Deny after inspection</option>
                </select>
              </label>
              {outcome === 'REDUCE' && (
                <label className="block text-sm font-medium text-gray-700">
                  {agreedExternalResolution ? 'Assessed item value after inspection' : 'Approved amount after inspection'}
                  <NonNegativeNumberInput
                    min="0"
                    step={String(10 ** -getReturnCurrencyDigits(currency))}
                    value={approvedAmount}
                    disabled={saving || saved}
                    onChange={(event) => setApprovedAmount(event.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
              )}
              <label className="block text-sm font-medium text-gray-700">
                {outcome === 'DENY'
                  ? 'Customer-facing reason for denial'
                  : 'Inspection notes'}
                <textarea
                  value={notes}
                  maxLength={2000}
                  disabled={saving || saved}
                  onChange={(event) => setNotes(event.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              {outcome === 'DENY' && (
                <p className="text-xs text-gray-600">
                  Shown to the customer when this return is denied.
                </p>
              )}
              <Button
                disabled={saving || saved}
                isLoading={saving}
                onClick={submit}
              >
                Save inspection decision
              </Button>
              {saved && (
                <p role="status" className="text-sm text-green-700">
                  Inspection saved. Refreshing the current return.
                </p>
              )}
            </>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
